<template>
  <div class="alerts-table-outer-wrapper d-flex" style="height: fit-content">
    <v-container
      class="mb-16 mt-2"
      fluid
      :dir="$i18n.locale === 'he' ? 'rtl' : 'ltr'"
    >
      <v-row justify="center">
        <v-col cols="12" md="10" lg="8">
          <!-- Alert Removal Notification -->
          <AlertNotification
            :show="showRemovalNotification"
            :message="notificationMessage"
            @close="closeRemovalNotification"
            @update:show="showRemovalNotification = $event"
          />

          <!-- Main Content Tabs -->
          <TabNavigation v-model="activeTab">
            <AlertsTab
              v-if="activeTab === 'alerts'"
              class="mt-3"
              :search="search"
              :filters="filters"
              :loading="loading"
              @update:search="search = $event"
              @update:filters="handleFiltersUpdate"
              @clear-filters="clearAllFilters"
            />
            <MaintenanceTab v-else-if="activeTab === 'maintenance'" />
          </TabNavigation>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import AlertNotification from "../ui/AlertNotification.vue";
import TabNavigation from "../common/TabNavigation.vue";
import AlertsTab from "./AlertsTab.vue";
import MaintenanceTab from "../maintenance/MaintenanceTab.vue";

export default {
  name: "AlertsTable",
  components: {
    AlertNotification,
    TabNavigation,
    AlertsTab,
    MaintenanceTab,
  },
  data: () => ({
    search: "",
    loading: false,
    filters: {
      status: null,
      type: null,
      severity: null,
      location: null,
    },
    activeTab: "alerts",
    showRemovalNotification: false,
    removedAlertId: null,
    previousAlertCount: 0,
    previousAlerts: [], // Store previous alerts array
  }),
  mounted() {
    // Initialize data and realtime after authentication
    if (this.isAuthenticated) {
      this.loading = true;
      Promise.resolve()
        .then(async () => {
          if (!this.alerts || this.alerts.length === 0) {
            await this.loadAlertsFromCache();
          }
          await this.loadLastKeepAliveFromCache();
          if (!this.isWebSocketConnected) {
            this.connectWebSocket();
          }
          this.startStatisticsMonitoring();
        })
        .finally(() => {
          this.loading = false;
        });
    }
  },
  computed: {
    ...mapGetters(["alerts", "isAuthenticated", "isWebSocketConnected"]),
    notificationMessage() {
      if (this.removedAlertId) {
        // Extract only numeric characters from the ID
        const alertId = this.extractNumericId(this.removedAlertId);
        return this.$t("alertRemovedWithId").replace("identifier", alertId);
      }
      return this.$t("alertRemoved");
    },
  },
  watch: {
    _activeTab: {
      handler(newVal) {
        // Tab changed - no need to log anymore
      },
      immediate: true,
    },
    alerts: {
      handler(newAlerts) {
        // Check if alerts were removed
        if (
          newAlerts.length < this.previousAlertCount &&
          this.previousAlerts.length > 0
        ) {
          // Find which alert was removed by comparing arrays
          const removedAlert = this.previousAlerts.find(
            (oldAlert) =>
              !newAlerts.some(
                (newAlert) => newAlert.identifier === oldAlert.identifier
              )
          );

          if (removedAlert) {
            this.removedAlertId = removedAlert.identifier;
            this.showRemovalNotification = true;
          } else {
            this.showRemovalNotification = true;
          }
        }

        // Update previous state
        this.previousAlertCount = newAlerts.length;
        this.previousAlerts = [...newAlerts]; // Create a copy of the array
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    ...mapActions([
      "loadAlertsFromCache",
      "loadLastKeepAliveFromCache",
      "connectWebSocket",
      "startStatisticsMonitoring",
    ]),
    closeRemovalNotification() {
      this.showRemovalNotification = false;
      this.removedAlertId = null;
    },
    clearAllFilters() {
      this.filters = {
        status: null,
        type: null,
        severity: null,
        location: null,
      };
      this.search = "";
    },
    handleFiltersUpdate(newFilters) {
      console.log("AlertsTable received filters update:", newFilters);
      this.filters = newFilters;
    },
    extractNumericId(id) {
      // Extract only numeric characters from the ID
      const numericPart = id.toString().replace(/\D/g, "");
      return numericPart || "0"; // Return "0" if no numbers found
    },
    getLastFourDigits(id) {
      // Extract only numeric characters and get last 4 digits
      const numericPart = this.extractNumericId(id);
      const result = numericPart.slice(-4);
      return result || "0000"; // Return "0000" if no numbers found
    },
    // Test function to manually trigger notification
    testRemovalNotification() {
      // Simulate alert removal by temporarily reducing the count
      const originalCount = this.alerts.length;
      this.previousAlertCount = originalCount + 1;
      this.previousAlerts = [
        ...this.alerts,
        { identifier: "123456789", headline: "Test Alert" },
      ];

      // Trigger the watcher logic with numeric ID
      this.removedAlertId = "123456789";
      this.showRemovalNotification = true;

      // Reset after a short delay
      setTimeout(() => {
        this.previousAlertCount = originalCount;
        this.previousAlerts = [...this.alerts];
      }, 100);
    },
  },
};
</script>

<style scoped>
.alerts-table-outer-wrapper {
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
}
@media (max-width: 768px) {
  .alerts-table-outer-wrapper {
    width: 100vw !important;
    max-width: 100vw !important;
    margin: 0 !important;
    padding: 0 !important;
    left: 0 !important;
    right: 0 !important;
  }
  .v-container, .v-row, .v-col {
    padding-left: 0 !important;
    padding-right: 0 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    width: 100vw !important;
    max-width: 100vw !important;
  }
}
</style>
